import { useEffect, useMemo, useState } from "react";
import { StickyNote, Plus, Trash2, Search, CheckCircle2, Circle, Loader2, User } from "lucide-react";
import api from "../lib/api";
import { HEADER_BG } from "../lib/constants";
import { useAuth } from "../lib/auth";
import ConfirmDialog from "../components/ConfirmDialog";

const CATEGORIES = [
  { key: "vale", label: "Vale", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "lembrete", label: "Lembrete", color: "bg-blue-100 text-blue-800 border-blue-200" },
  { key: "geral", label: "Geral", color: "bg-slate-100 text-slate-700 border-slate-200" },
];

const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function NotesView() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", category: "vale" });
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("all"); // all | vale | peca | lembrete | geral
  const [showResolved, setShowResolved] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/notes");
      setNotes(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erro ao carregar anotações.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.content.trim()) return setErr("Escreva o conteúdo da anotação.");
    setSaving(true); setErr("");
    try {
      const r = await api.post("/notes", form);
      setNotes((prev) => [r.data, ...prev]);
      setForm({ title: "", content: "", category: form.category });
    } catch (e) {
      setErr(e.response?.data?.detail || "Erro ao criar.");
    } finally { setSaving(false); }
  };

  const toggleResolved = async (n) => {
    try {
      const r = await api.patch(`/notes/${n.id}`, { resolved: !n.resolved });
      setNotes((prev) => prev.map((x) => (x.id === n.id ? r.data : x)));
    } catch (e) { setErr(e.response?.data?.detail || "Erro."); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/notes/${id}`);
      setNotes((prev) => prev.filter((x) => x.id !== id));
    } catch (e) { setErr(e.response?.data?.detail || "Erro ao excluir."); }
  };

  const filtered = useMemo(() => {
    return notes.filter((n) => {
      if (!showResolved && n.resolved) return false;
      if (filterCat !== "all" && n.category !== filterCat) return false;
      const s = q.toLowerCase().trim();
      if (!s) return true;
      return (
        (n.title || "").toLowerCase().includes(s) ||
        (n.content || "").toLowerCase().includes(s) ||
        (n.createdBy || "").toLowerCase().includes(s)
      );
    });
  }, [notes, q, filterCat, showResolved]);

  const counts = useMemo(() => {
    const map = { total: 0, pending: 0, byCat: { vale: 0, lembrete: 0, geral: 0 } };
    for (const n of notes) {
      map.total += 1;
      if (!n.resolved) map.pending += 1;
      if (map.byCat[n.category] !== undefined) map.byCat[n.category] += (n.resolved ? 0 : 1);
    }
    return map;
  }, [notes]);

  return (
    <div className="p-6 space-y-4" data-testid="notes-view">
      <div>
        <h2 className="font-heading text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
          <StickyNote className="w-6 h-6 text-blue-600" /> Anotações
        </h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Registre vales, se alguém pegou peça emprestada, lembretes e outras notas do dia a dia.
        </p>
      </div>

      {/* New note form */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3">
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Título (opcional) — ex.: 'Vale do Kevin R$100'"
              data-testid="note-title"
              className="h-10 rounded-md border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
            />
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="note-category"
              className="h-10 rounded-md border border-slate-300 px-3 text-sm bg-white focus:border-blue-500 focus:outline-none"
            >
              {CATEGORIES.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
            </select>
          </div>
          <textarea
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            placeholder="Descreva a anotação... ex.: 'Peguei R$100 do caixa como vale'"
            rows={3}
            data-testid="note-content"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none resize-none"
          />
          {err && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</div>
          )}
          <div className="flex justify-between items-center flex-wrap gap-2">
            <span className="text-[11px] text-slate-400">
              Anotação criada por <b className="text-slate-600">{user?.name || user?.username}</b>
            </span>
            <button
              onClick={create}
              disabled={saving || !form.content.trim()}
              data-testid="note-save"
              className="h-10 px-5 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Adicionar anotação
            </button>
          </div>
        </div>
      </div>

      {/* Filters + counters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar título, conteúdo ou autor..."
            data-testid="notes-search"
            className="w-full h-9 rounded-lg border border-slate-300 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
        <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
          {[{ key: "all", label: "Todas" }, ...CATEGORIES].map((c) => (
            <button
              key={c.key}
              onClick={() => setFilterCat(c.key)}
              data-testid={`filter-cat-${c.key}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all inline-flex items-center gap-1.5 ${
                filterCat === c.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
              }`}
            >
              {c.label}
              {c.key !== "all" && counts.byCat[c.key] > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-blue-100 text-blue-700 text-[9px] font-bold">
                  {counts.byCat[c.key]}
                </span>
              )}
            </button>
          ))}
        </div>
        <label className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-pointer" data-testid="show-resolved-label">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
            className="w-3.5 h-3.5 accent-blue-600"
            data-testid="show-resolved"
          />
          Mostrar resolvidas
        </label>
      </div>

      {/* Notes list */}
      {loading ? (
        <div className="text-sm text-slate-400 py-8 text-center">Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-12 text-center">
          <StickyNote className="w-10 h-10 mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">
            {notes.length === 0 ? "Nenhuma anotação ainda. Registre sua primeira acima!" : "Nenhuma anotação neste filtro."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((n) => {
            const cat = CAT_MAP[n.category] || CAT_MAP.geral;
            return (
              <div key={n.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
                n.resolved ? "border-slate-200 opacity-60" : "border-slate-200"
              }`} data-testid={`note-${n.id}`}>
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <button
                      onClick={() => toggleResolved(n)}
                      data-testid={`note-toggle-${n.id}`}
                      title={n.resolved ? "Marcar como pendente" : "Marcar como resolvida"}
                      className="text-slate-400 hover:text-emerald-600"
                    >
                      {n.resolved ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4" />}
                    </button>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded font-semibold text-[10px] border ${cat.color}`}>
                      {cat.label}
                    </span>
                    {n.title && (
                      <span className={`font-heading font-semibold text-sm truncate ${n.resolved ? "line-through text-slate-500" : "text-slate-900"}`}>
                        {n.title}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setConfirmDel(n)}
                    data-testid={`note-delete-${n.id}`}
                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className={`px-4 py-3 whitespace-pre-wrap text-sm ${n.resolved ? "line-through text-slate-400" : "text-slate-700"}`}>
                  {n.content}
                </div>
                <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {n.createdBy || "—"}
                  </span>
                  <span className="tabular-nums">{formatDate(n.createdAt)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDel}
        title="Excluir anotação"
        message={confirmDel ? `Excluir a anotação "${confirmDel.title || confirmDel.content.slice(0, 40)}"?` : ""}
        confirmLabel="Excluir"
        onCancel={() => setConfirmDel(null)}
        onConfirm={async () => { const id = confirmDel.id; setConfirmDel(null); await del(id); }}
      />
    </div>
  );
}
